import { Card, Descriptions, List, Space, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { DocumentSummary } from '../../types/ai-document-summary';

const { Text, Title } = Typography;

const colorToTag: Record<DocumentSummary['statusColor'], string> = {
  green: 'success',
  amber: 'warning',
  red: 'error',
  gray: 'default',
};

const colorIcon: Record<DocumentSummary['statusColor'], React.ReactNode> = {
  green: <CheckCircleOutlined />,
  amber: <ClockCircleOutlined />,
  red: <CloseCircleOutlined />,
  gray: <ExclamationCircleOutlined />,
};

export interface SummaryCardProps {
  summary: DocumentSummary;
  /** When true, uses compact list layout for key facts. */
  compact?: boolean;
}

/**
 * 360° document summary display (grounded tool output — no raw JSON to the user).
 */
export function SummaryCard({ summary, compact }: SummaryCardProps) {
  const { formatCurrency } = useCurrency();
  return (
    <Card size="small" className="ai-agent-summary-card" styles={{ body: { padding: compact ? 10 : 14 } }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {summary.title}
          </Title>
          <Space size="small" wrap>
            <Text type="secondary">
              {summary.docType} · {summary.docId}
            </Text>
            <Tag color={colorToTag[summary.statusColor]} icon={colorIcon[summary.statusColor]}>
              {summary.status}
            </Tag>
          </Space>
        </div>

        {summary.keyFacts.length > 0 && (
          <Descriptions
            size="small"
            column={compact ? 1 : 2}
            layout="vertical"
            items={summary.keyFacts.map((f) => ({
              key: f.label,
              label: f.label,
              children: f.value,
            }))}
          />
        )}

        {summary.financialImpact && summary.financialImpact.length > 0 && (
          <div>
            <Text strong>Financial</Text>
            <List
              size="small"
              dataSource={summary.financialImpact}
              renderItem={(row) => (
                <List.Item>
                  {row.label}: {formatCurrency(row.amount, row.currency)}
                </List.Item>
              )}
            />
          </div>
        )}

        {summary.linkedDocs.length > 0 && (
          <div>
            <Text strong>Linked</Text>
            <List
              size="small"
              dataSource={summary.linkedDocs}
              renderItem={(d) => (
                <List.Item>
                  {d.type} · {d.status} <Text type="secondary" code children={d.id} />
                </List.Item>
              )}
            />
          </div>
        )}

        {summary.pendingActions.length > 0 && (
          <div>
            <Text strong>Pending</Text>
            <List
              size="small"
              dataSource={summary.pendingActions}
              renderItem={(a) => (
                <List.Item>
                  {a.action}
                  {a.owner ? <Text type="secondary"> — {a.owner}</Text> : null}
                  {a.dueDate ? <Text type="secondary"> · due {a.dueDate}</Text> : null}
                </List.Item>
              )}
            />
          </div>
        )}

        {summary.blockers.length > 0 && (
          <div>
            <Text strong type="danger">
              Blockers
            </Text>
            <List
              size="small"
              dataSource={summary.blockers}
              renderItem={(b) => (
                <List.Item>
                  {b.reason} — {b.resolution}
                </List.Item>
              )}
            />
          </div>
        )}

        {summary.nextBestActions.length > 0 && (
          <div>
            <Text strong>Next steps</Text>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
              {summary.nextBestActions.map((n, i) => (
                <li key={i}>
                  {n.label}
                  {n.navigateTo ? <Text type="secondary"> → {n.navigateTo}</Text> : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Text type="secondary" style={{ fontSize: 12 }}>
          Generated {new Date(summary.generatedAt).toLocaleString()}
        </Text>
      </Space>
    </Card>
  );
}
